import React from 'react';
import { XIcon } from '@heroicons/react/outline';

function RequestDetailsModal({ isOpen, onClose, request }) {
  if (!isOpen || !request) return null;

  // Format date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Get status badge styling
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'fulfilled':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 overflow-y-auto z-50">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={onClose}
          aria-hidden="true"
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6">
          {/* Header with close button */}
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Request Details
            </h3>
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <XIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          
          {/* Request ID and Status */}
          <div className="mb-6">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Request ID: {request.id}</span>
              <span 
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold leading-5 ${getStatusBadgeClass(request.status)}`}
              >
                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
              </span>
            </div>
          </div>

          {/* Request details in sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left column */}
            <div>
              {/* Food Item Information */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Food Item Information
                </h4>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  {request.listing ? (
                    <>
                      <h5 className="text-lg font-medium text-gray-900">{request.listing.title}</h5>
                      <dl className="mt-2 text-sm">
                        <div className="flex justify-between my-1">
                          <dt className="font-medium text-gray-500">Category:</dt>
                          <dd>{request.listing.category}</dd>
                        </div>
                        <div className="flex justify-between my-1">
                          <dt className="font-medium text-gray-500">Quantity Available:</dt>
                          <dd>{request.listing.quantity}</dd>
                        </div>
                        <div className="flex justify-between my-1">
                          <dt className="font-medium text-gray-500">Requested:</dt>
                          <dd>{request.quantity}</dd>
                        </div>
                        <div className="flex justify-between my-1">
                          <dt className="font-medium text-gray-500">Expires:</dt>
                          <dd>{request.listing.expiry ? formatDate(request.listing.expiry).split(',')[0] : 'N/A'}</dd>
                        </div>
                        {request.listing.description && (
                          <div className="mt-2">
                            <dt className="font-medium text-gray-500">Description:</dt>
                            <dd className="mt-1 whitespace-pre-line">{request.listing.description}</dd>
                          </div>
                        )}
                      </dl>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">No listing information available</p>
                  )}
                </div>
              </div>

              {/* Donor Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Donor Information
                </h4>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  {request.donor ? (
                    <>
                      <h5 className="text-lg font-medium text-gray-900">{request.donor.name}</h5>
                      {request.donor.organization && (
                        <p className="text-sm text-gray-600">{request.donor.organization}</p>
                      )}
                      <dl className="mt-2 text-sm">
                        {request.donor.email && (
                          <div className="flex justify-between my-1">
                            <dt className="font-medium text-gray-500">Email:</dt>
                            <dd>{request.donor.email}</dd>
                          </div>
                        )}
                        {request.donor.phone && (
                          <div className="flex justify-between my-1">
                            <dt className="font-medium text-gray-500">Phone:</dt>
                            <dd>{request.donor.phone}</dd>
                          </div>
                        )}
                        {request.donor.location && (
                          <div className="flex justify-between my-1">
                            <dt className="font-medium text-gray-500">Location:</dt>
                            <dd>{request.donor.location}</dd>
                          </div>
                        )}
                      </dl>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">No donor information available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right column */}
            <div>
              {/* Recipient Information */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Recipient Information
                </h4>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  {request.recipient ? (
                    <>
                      <h5 className="text-lg font-medium text-gray-900">{request.recipient.name}</h5>
                      {request.recipient.organization && (
                        <p className="text-sm text-gray-600">{request.recipient.organization}</p>
                      )}
                      <dl className="mt-2 text-sm">
                        {request.recipient.email && (
                          <div className="flex justify-between my-1">
                            <dt className="font-medium text-gray-500">Email:</dt>
                            <dd>{request.recipient.email}</dd>
                          </div>
                        )}
                        {request.recipient.phone && (
                          <div className="flex justify-between my-1">
                            <dt className="font-medium text-gray-500">Phone:</dt>
                            <dd>{request.recipient.phone}</dd>
                          </div>
                        )}
                        {request.recipient.location && (
                          <div className="flex justify-between my-1">
                            <dt className="font-medium text-gray-500">Location:</dt>
                            <dd>{request.recipient.location}</dd>
                          </div>
                        )}
                      </dl>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">No recipient information available</p>
                  )}
                </div>
              </div>

              {/* Request Timeline */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Request Timeline
                </h4>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <dl className="mt-1 text-sm">
                    <div className="flex justify-between my-1">
                      <dt className="font-medium text-gray-500">Created:</dt>
                      <dd>{formatDate(request.createdAt)}</dd>
                    </div>
                    {request.approvedAt && (
                      <div className="flex justify-between my-1">
                        <dt className="font-medium text-gray-500">Approved:</dt>
                        <dd>{formatDate(request.approvedAt)}</dd>
                      </div>
                    )}
                    {request.rejectedAt && (
                      <div className="flex justify-between my-1">
                        <dt className="font-medium text-gray-500">Rejected:</dt>
                        <dd>{formatDate(request.rejectedAt)}</dd>
                      </div>
                    )}
                    {request.fulfilledAt && (
                      <div className="flex justify-between my-1">
                        <dt className="font-medium text-gray-500">Fulfilled:</dt>
                        <dd>{formatDate(request.fulfilledAt)}</dd>
                      </div>
                    )}
                    {request.notes && (
                      <div className="mt-2">
                        <dt className="font-medium text-gray-500">Notes:</dt>
                        <dd className="mt-1 whitespace-pre-line">{request.notes}</dd>
                      </div>
                    )}
                    {request.rejectionReason && (
                      <div className="mt-2">
                        <dt className="font-medium text-red-500">Rejection Reason:</dt>
                        <dd className="mt-1 text-red-600 whitespace-pre-line">{request.rejectionReason}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RequestDetailsModal;
